import { useCallback, useEffect, useState } from "react";
import { Button, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import useFetch from "../../../../hooks/useFetch";
import Select from "react-select";

//interfaces
import { SucursalInterface } from "../../../../interfaces/SucursalInterface";
import { EstadoInterface } from "../../../../interfaces/EstadoInterface";
import { useGetData } from "../../../../hooks/useGetData";
import ForbiddenMessage from "../../../Mensajes/ForbiddenMessage";
import SuccessMessage from "../../../Mensajes/SuccessMessage";
import ServerMessage from "../../../Mensajes/ServerMessage";

//estilos
const classes = require("./SucursalEdit.module.css").default;

/**
 * SucursalEdit Component
 * @description: Componente que permite editar una sucursal.
 * @date [fecha actual]
 * @param Props Ninguno
 * @returns JSX del formulario de edición de sucursal.
 */

const SucursalEdit = () => {
  const { idSucursal } = useParams();
  const navigate = useNavigate();
  const { sendRequest, setError, error, serverMessage, setServerMessage } =
    useFetch();
  const [sucursal, setSucursal] = useState<SucursalInterface>();
  const [show, setShow] = useState(false);
  const { data: estados } = useGetData("/estado", "estado");
  const [estado, setEstado] = useState<number>();
  const [correos, setCorreos] = useState<string[]>([""]);

  //Variables de formulario
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
    setValue,
  } = useForm({
    defaultValues: {
      nombre: "",
      domicilio: "",
      latitud: "",
      longitud: "",
      id_nmp: "",
      correo: "",
      estatus: true,
      estado_id: "",
    },
  });

  //Función que trata el detalle de sucursal.
  const transformData = useCallback(
    (variable: any) => {
      setSucursal(variable.data);
      setEstado(variable.data.estado.id);
      
      // Establecer valores del formulario
      setValue("nombre", variable.data.nombre);
      setValue("domicilio", variable.data.domicilio);
      setValue("latitud", variable.data.latitud.toString());
      setValue("longitud", variable.data.longitud.toString());
      setValue("id_nmp", variable.data.id_nmp || "");
      setValue("correo", variable.data.correo || "");
      setValue("estatus", variable.data.estatus);
      setValue("estado_id", variable.data.estado.id.toString());
      // Cargar correos dinámicos
      if (variable.data.correo) {
        setCorreos(variable.data.correo.split(",").map((c: string) => c.trim()));
      } else {
        setCorreos([""]);
      }
    },
    [setValue]
  );

  //Se recibe la sucursal a editar
  useEffect(() => {
    sendRequest(
      {
        url: `/sucursal/${idSucursal}`,
      },
      transformData
    );
  }, [sendRequest, transformData, idSucursal]);

  //Función que hace el PATCH para editar la sucursal.
  const editSucursal = (data: any) => {
    setError(null);
    setServerMessage(undefined);
    const body = {
      nombre: data.nombre,
      domicilio: data.domicilio,
      latitud: parseFloat(data.latitud),
      longitud: parseFloat(data.longitud),
      estado_id: estado,
      id_nmp: data.id_nmp || null,
      correo: correos.filter(c => c.trim() !== "").join(",") || null,
      estatus: data.estatus,
    };

    sendRequest(
      {
        url: `/sucursal/${idSucursal}`,
        method: "PATCH",
        body: body,
      },
      () => {
        setShow(true);
      }
    );
  };

  //Mientras se carga el contenido se muestra un spinner
  if (!sucursal) {
    return (
      <div className={classes.spinnerContainer}>
        <Spinner animation="grow" variant="info" />
      </div>
    );
  }

  //Si no tiene permisos para editar sucursales
  if (error === 403) {
    return <ForbiddenMessage onCloseHandler={() => setError(null)} />;
  }

  return (
    <Container style={{ marginTop: "20px" }}>
      <h2>Editar sucursal: {sucursal.nombre}</h2>
      <Form onSubmit={handleSubmit(editSucursal)}>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Nombre de la sucursal *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ingrese el nombre de la sucursal"
                {...register("nombre", {
                  required: "El nombre es obligatorio",
                  minLength: {
                    value: 3,
                    message: "El nombre debe tener al menos 3 caracteres",
                  },
                })}
                isInvalid={!!errors.nombre}
              />
              <Form.Control.Feedback type="invalid">
                {errors.nombre?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Domicilio *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Ingrese el domicilio completo"
                {...register("domicilio", {
                  required: "El domicilio es obligatorio",
                  minLength: {
                    value: 10,
                    message: "El domicilio debe tener al menos 10 caracteres",
                  },
                })}
                isInvalid={!!errors.domicilio}
              />
              <Form.Control.Feedback type="invalid">
                {errors.domicilio?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>ID de Sucursal</Form.Label>
              <Form.Control
                type="number"
                placeholder="Ingrese el ID de la sucursal (opcional)"
                {...register("id_nmp", {
                  maxLength: {
                    value: 10,
                    message: "El ID no puede tener más de 10 dígitos",
                  },
                  pattern: {
                    value: /^[0-9]*$/,
                    message: "Solo se permiten números",
                  },
                })}
                isInvalid={!!errors.id_nmp}
                onInput={(e: any) => {
                  e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                }}
              />
              <Form.Control.Feedback type="invalid">
                {errors.id_nmp?.message}
              </Form.Control.Feedback>
            </Form.Group>

            {(sucursal as any).plataforma_id === 2 && (
              <Form.Group className="mb-3">
                <Form.Label>Correos para envío de orden de servicio</Form.Label>
                {correos.map((correo, index) => (
                  <div key={index} className="d-flex align-items-center mb-2">
                    <Form.Control
                      type="email"
                      placeholder="correo@ejemplo.com"
                      value={correo}
                      onChange={(e) => {
                        const nuevosCorreos = [...correos];
                        nuevosCorreos[index] = e.target.value;
                        setCorreos(nuevosCorreos);
                      }}
                      isInvalid={correo !== "" && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(correo)}
                    />
                    {correos.length > 1 && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="ms-2"
                        onClick={() => {
                          if (window.confirm(`¿Eliminar el correo "${correo}"?`)) {
                            setCorreos(correos.filter((_, i) => i !== index));
                          }
                        }}
                      >
                        ✕
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setCorreos([...correos, ""])}
                >
                  + Agregar correo
                </Button>
                <Form.Text className="text-muted d-block mt-1">
                  La orden de servicio se enviará automáticamente a estos correos al finalizar.
                </Form.Text>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Estado *</Form.Label>
              <Controller
                name="estado_id"
                control={control}
                rules={{ required: "Debe seleccionar un estado" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={estados?.map((estado: EstadoInterface) => ({
                      value: estado.id,
                      label: estado.nombre,
                    }))}
                    placeholder="Seleccione un estado"
                    onChange={(selectedOption: any) => {
                      setEstado(selectedOption?.value);
                      field.onChange(selectedOption?.value);
                    }}
                    value={
                      estado
                        ? {
                            value: estado,
                            label: estados?.find((e: EstadoInterface) => e.id === estado)?.nombre,
                          }
                        : null
                    }
                  />
                )}
              />
              {errors.estado_id && (
                <Form.Text className="text-danger">
                  {errors.estado_id.message}
                </Form.Text>
              )}
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Latitud *</Form.Label>
              <Form.Control
                type="number"
                step="any"
                placeholder="Ej: 16.754888"
                {...register("latitud", {
                  required: "La latitud es obligatoria",
                  min: {
                    value: -90,
                    message: "La latitud debe estar entre -90 y 90",
                  },
                  max: {
                    value: 90,
                    message: "La latitud debe estar entre -90 y 90",
                  },
                })}
                isInvalid={!!errors.latitud}
              />
              <Form.Control.Feedback type="invalid">
                {errors.latitud?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Longitud *</Form.Label>
              <Form.Control
                type="number"
                step="any"
                placeholder="Ej: -93.124747"
                {...register("longitud", {
                  required: "La longitud es obligatoria",
                  min: {
                    value: -180,
                    message: "La longitud debe estar entre -180 y 180",
                  },
                  max: {
                    value: 180,
                    message: "La longitud debe estar entre -180 y 180",
                  },
                })}
                isInvalid={!!errors.longitud}
              />
              <Form.Control.Feedback type="invalid">
                {errors.longitud?.message}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Estado de la sucursal</Form.Label>
              <div className="d-flex justify-content-center">
                <Form.Check
                  type="switch"
                  id="estatus-switch-edit"
                  label="Sucursal activa"
                  {...register("estatus")}
                />
              </div>
            </Form.Group>

            <div className="mt-4">
              <small className="text-muted">
                <strong>Ubicación actual:</strong>
                <br />
                <a 
                  href={`https://www.google.com/maps?q=${sucursal.latitud},${sucursal.longitud}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-info btn-sm"
                >
                  Ver ubicación actual en Google Maps
                </a>
              </small>
            </div>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col>
            <Button type="submit" variant="warning" className="me-2">
              Actualizar Sucursal
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="me-2"
              onClick={() => {
                // Resetear a los valores originales
                setValue("nombre", sucursal.nombre);
                setValue("domicilio", sucursal.domicilio);
                setValue("latitud", sucursal.latitud.toString());
                setValue("longitud", sucursal.longitud.toString());
                setValue("correo", sucursal.correo || "");
                setValue("estatus", sucursal.estatus);
                setValue("estado_id", sucursal.estado.id.toString());
                setEstado(sucursal.estado.id);
                setCorreos(sucursal.correo ? sucursal.correo.split(",").map(c => c.trim()) : [""]);
                setError(null);
                setServerMessage(undefined);
              }}
            >
              Restaurar
            </Button>
            <Button
              type="button"
              variant="outline-secondary"
              onClick={() => navigate(-1)}
            >
              Cancelar
            </Button>
          </Col>
        </Row>
      </Form>

      {/* Mensajes de respuesta */}
      {show && (
        <SuccessMessage 
          header="¡Éxito!" 
          body="Sucursal actualizada correctamente" 
          onCloseHandler={() => setShow(false)}
        />
      )}
      {serverMessage && (
        <ServerMessage 
          message={serverMessage} 
          onCloseHandler={() => setServerMessage(undefined)}
        />
      )}
    </Container>
  );
};

export default SucursalEdit;
